// Overlays & navigation — board section 05: everything that opens on top of the
// page (Modal, ConfirmModal, Drawer, the Dropdown family) plus the two tab
// strips, closing with the icon sampler.

import { useState } from 'react';
import {
  Button,
  Chip,
  ConfirmModal,
  Drawer,
  Dropdown,
  Field,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  Modal,
  MultiSectionCheckboxDropdown,
  SegmentedControl,
  SortDropdown,
  Tabs,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
  type ModalSize,
  type ModalTone,
  type SortValue,
} from '@/components/common';
import { ICON_SAMPLER } from '../tokens';
import Spec from './Spec';

const MODAL_SIZES: ModalSize[] = ['sm', 'md', 'lg'];
const MODAL_TONES: ModalTone[] = ['brand', 'warning', 'danger', 'success'];

const TONE_ICON: Record<ModalTone, string> = {
  brand: 'tag',
  warning: 'triangle-alert',
  danger: 'trash-2',
  success: 'circle-check',
};

/** One trigger per size — the panel reopens at whichever size was pressed. */
const ModalSizeDemo = () => {
  const [size, setSize] = useState<ModalSize | null>(null);
  return (
    <>
      <div className='theme-page__variants'>
        {MODAL_SIZES.map((s) => (
          <Spec key={s} label={`size='${s}'`}>
            <Button variant='secondary' iconStart='panel-left' onClick={() => setSize(s)}>
              Open {s}
            </Button>
          </Spec>
        ))}
      </div>
      <Modal
        open={size !== null}
        onClose={() => setSize(null)}
        size={size ?? 'md'}
        icon='tag'
        title={`Example modal · ${size ?? ''}`}
        subtitle='Overlay + centered panel with an icon-tile header.'
        footer={
          <>
            <Button variant='secondary' onClick={() => setSize(null)}>
              Cancel
            </Button>
            <Button iconStart='check' onClick={() => setSize(null)}>
              Confirm
            </Button>
          </>
        }
      >
        <p style={{ margin: 0 }}>
          Scrollable body and a footer action row. Closes on Escape, the X button, or the overlay.
        </p>
      </Modal>
    </>
  );
};

/** `tone` tints the header icon tile only — the tile needs an `icon` to show. */
const ModalToneDemo = () => {
  const [tone, setTone] = useState<ModalTone | null>(null);
  return (
    <>
      <div className='theme-page__variants'>
        {MODAL_TONES.map((t) => (
          <Spec key={t} label={`tone='${t}'`}>
            <Button variant='secondary' iconStart={TONE_ICON[t]} onClick={() => setTone(t)}>
              {t}
            </Button>
          </Spec>
        ))}
      </div>
      <Modal
        open={tone !== null}
        onClose={() => setTone(null)}
        tone={tone ?? 'brand'}
        icon={tone ? TONE_ICON[tone] : 'tag'}
        title={`tone='${tone ?? ''}'`}
        closeOnOverlay={false}
        footer={
          <Button variant='secondary' onClick={() => setTone(null)}>
            Close
          </Button>
        }
      >
        <p style={{ margin: 0 }}>
          This one sets <code>closeOnOverlay={'{false}'}</code> — only Escape or a button closes it.
        </p>
      </Modal>
    </>
  );
};

const ConfirmModalDemo = () => {
  const [open, setOpen] = useState<'danger' | 'brand' | null>(null);
  const danger = open === 'danger';
  return (
    <>
      <div className='theme-page__variants'>
        <Spec label="tone='danger' confirmVariant='danger'">
          <Button variant='danger' iconStart='trash-2' onClick={() => setOpen('danger')}>
            Delete site
          </Button>
        </Spec>
        <Spec label="tone='brand' (defaults)">
          <Button variant='secondary' iconStart='send' onClick={() => setOpen('brand')}>
            Publish form
          </Button>
        </Spec>
      </div>
      <ConfirmModal
        open={open !== null}
        onClose={() => setOpen(null)}
        onConfirm={() => setOpen(null)}
        tone={danger ? 'danger' : 'brand'}
        icon={danger ? 'trash-2' : 'send'}
        confirmVariant={danger ? 'danger' : 'primary'}
        title={danger ? 'Delete this site?' : 'Publish this form?'}
        description={
          danger
            ? 'Warehouse North and its 12 submissions will be removed. This cannot be undone.'
            : 'Crew members will see this form on their next sync.'
        }
        confirmLabel={danger ? 'Delete' : 'Publish'}
      />
    </>
  );
};

const DrawerDemo = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant='secondary' iconStart='panel-right' onClick={() => setOpen(true)}>
        Open drawer
      </Button>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        icon='map-pin'
        title='Warehouse North'
        subtitle='Site details'
        footer={
          <>
            <Button variant='secondary' onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button iconStart='check' onClick={() => setOpen(false)}>
              Save
            </Button>
          </>
        }
      >
        <div className='theme-page__col'>
          <Field label='Site name'>
            <Input defaultValue='Warehouse North' />
          </Field>
          <Field label='Crew' hint='Anchored to the inline-end edge — it flips under RTL.'>
            <Input defaultValue='Crew A' />
          </Field>
        </div>
      </Drawer>
    </>
  );
};

/** Dropdown is compose-first: the caller owns the trigger and the rows. */
const DropdownDemo = ({
  align = 'end',
  portal = false,
}: {
  align?: 'start' | 'end';
  portal?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Dropdown
      open={open}
      onClose={() => setOpen(false)}
      align={align}
      portal={portal}
      trigger={
        <IconButton
          icon='ellipsis-vertical'
          variant='ghost'
          aria-label='Row actions'
          onClick={() => setOpen((o) => !o)}
        />
      }
    >
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        onClick={() => setOpen(false)}
      >
        <Icon name='pencil' size={16} /> Edit
      </button>
      <button
        type='button'
        className='app-dropdown__item'
        role='menuitem'
        onClick={() => setOpen(false)}
      >
        <Icon name='copy' size={16} /> Duplicate
      </button>
      <div className='app-dropdown__divider' role='separator' />
      <button
        type='button'
        className='app-dropdown__item app-dropdown__item--danger'
        role='menuitem'
        onClick={() => setOpen(false)}
      >
        <Icon name='trash-2' size={16} /> Delete
      </button>
    </Dropdown>
  );
};

const FILTER_SECTIONS = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { id: 'complete', label: 'Complete' },
      { id: 'progress', label: 'In progress' },
      { id: 'overdue', label: 'Overdue' },
    ],
  },
  {
    key: 'crew',
    label: 'Crew',
    options: [
      { id: 'a', label: 'Crew A' },
      { id: 'b', label: 'Crew B' },
      { id: 'c', label: 'Crew C', disabled: true },
    ],
  },
];

const CheckboxDropdownDemo = ({ showCount = false }: { showCount?: boolean }) => {
  const [selected, setSelected] = useState<Record<string, Set<string>>>({
    status: new Set(['overdue']),
    crew: new Set<string>(),
  });
  const toggle = (section: string, id: string) =>
    setSelected((prev) => {
      const next = new Set(prev[section]);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...prev, [section]: next };
    });
  return (
    <MultiSectionCheckboxDropdown
      label='Filters'
      sections={FILTER_SECTIONS}
      selected={selected}
      onToggle={toggle}
      onClearSection={(key) => setSelected((prev) => ({ ...prev, [key]: new Set() }))}
      clearLabel='Clear'
      showCount={showCount}
    />
  );
};

const FILTER_CATEGORIES: FilterDropdownCategory[] = [
  {
    key: 'status',
    label: 'Status',
    icon: 'circle-dot',
    options: [
      { id: 'active', label: 'Active', dotColor: 'var(--success)' },
      { id: 'pending', label: 'Pending', dotColor: 'var(--warning)' },
      { id: 'archived', label: 'Archived', dotColor: 'var(--text-3)' },
    ],
  },
  {
    key: 'priority',
    label: 'Priority',
    icon: 'flag',
    options: [
      { id: 'high', label: 'High' },
      { id: 'medium', label: 'Medium' },
      { id: 'low', label: 'Low' },
    ],
  },
  {
    key: 'due',
    label: 'Due date',
    icon: 'calendar',
    type: 'single',
    rangeOptionId: 'custom',
    defaultOptionId: 'any',
    options: [
      { id: 'any', label: 'Any due date', icon: 'clock' },
      { id: 'none', label: 'No due date', icon: 'calendar-off' },
      { id: 'custom', label: 'Custom range', icon: 'calendar' },
    ],
  },
  {
    key: 'assignee',
    label: 'Assignee',
    icon: 'user',
    searchable: true,
    searchPlaceholder: 'Search people…',
    options: [
      { id: 'p1', label: 'Umer Shahbaz' },
      { id: 'p2', label: 'Omar Khalil' },
      { id: 'p3', label: 'Sara Nasser' },
      { id: 'p4', label: 'Hana Bishara' },
      { id: 'p5', label: 'Maya Said' },
      { id: 'p6', label: 'Dana Fakhoury' },
      { id: 'p7', label: 'Karim Mansour' },
      { id: 'p8', label: 'Huda Saleh' },
      { id: 'p9', label: 'Rami Aoun' },
      { id: 'p10', label: 'Lina Haddad' },
      { id: 'p11', label: 'Ziad Barakat' },
    ],
  },
  {
    key: 'tags',
    label: 'Tags',
    icon: 'tag',
    searchable: true,
    searchPlaceholder: 'Search tags…',
    options: [
      { id: 'audit', label: 'Audit' },
      { id: 'compliance', label: 'Compliance' },
      { id: 'energy', label: 'Energy' },
      { id: 'field', label: 'Field' },
      { id: 'fleet', label: 'Fleet' },
      { id: 'hvac', label: 'HVAC' },
      { id: 'maintenance', label: 'Maintenance' },
    ],
  },
];

const FilterDropdownDemo = ({ portal = false }: { portal?: boolean }) => {
  const [value, setValue] = useState<Record<string, Set<string>>>({
    assignee: new Set(['p2', 'p3']),
  });
  const [ranges, setRanges] = useState<Record<string, FilterDropdownDateRange>>({});
  return (
    <FilterDropdown
      label='Filter'
      categories={FILTER_CATEGORIES}
      value={value}
      ranges={ranges}
      onApply={(next, nextRanges) => {
        setValue(next);
        setRanges(nextRanges);
      }}
      emptyText='No matches'
      portal={portal}
    />
  );
};

const SORT_OPTIONS = [
  { key: 'site', label: 'Site name' },
  { key: 'due', label: 'Due date' },
  { key: 'findings', label: 'Findings' },
];

const SortDropdownDemo = ({ hideDirection = false }: { hideDirection?: boolean }) => {
  const [value, setValue] = useState<SortValue>({ key: 'site', type: 'asc' });
  return (
    <SortDropdown
      options={SORT_OPTIONS}
      value={value}
      onChange={setValue}
      hideDirection={hideDirection}
    />
  );
};

const TABS_ITEMS = [
  { value: 'all', label: 'All', icon: 'list-checks', count: 12 },
  { value: 'mine', label: 'Assigned to me', icon: 'user', count: 3 },
  { value: 'done', label: 'Completed', icon: 'circle-check', count: 0 },
];

const TabsDemo = () => {
  const [value, setValue] = useState('all');
  return (
    <Tabs items={TABS_ITEMS} value={value} onChange={setValue} aria-label='Inspection views' />
  );
};

const SEGMENTED_OPTIONS = [
  { value: 'tree', label: 'Tree', icon: 'network' },
  { value: 'chart', label: 'Chart', icon: 'workflow', count: 4 },
];

const SegmentedDemo = () => {
  const [value, setValue] = useState('tree');
  return (
    <SegmentedControl
      options={SEGMENTED_OPTIONS}
      value={value}
      onChange={setValue}
      aria-label='Structure view'
    />
  );
};

const SegmentedPlainDemo = () => {
  const [value, setValue] = useState('tree');
  return (
    <SegmentedControl
      variant='plain'
      options={SEGMENTED_OPTIONS}
      value={value}
      onChange={setValue}
      aria-label='Structure view (plain)'
    />
  );
};

// Icon-only segments: no `label` → square pill named by `ariaLabel`.
const SegmentedIconsDemo = () => {
  const [value, setValue] = useState('cards');
  return (
    <SegmentedControl
      options={[
        { value: 'cards', icon: 'layout-grid', ariaLabel: 'Cards view' },
        { value: 'table', icon: 'table-2', ariaLabel: 'Table view' },
      ]}
      value={value}
      onChange={setValue}
      aria-label='List view'
    />
  );
};

const OverlaysNavSection = () => (
  <section className='theme-page__section'>
    <h2 className='theme-page__section-title'>
      <Icon name='layers' size={18} /> 05 · Overlays &amp; navigation
    </h2>

    {/* ---- Modal ---- */}
    <h3 className='theme-page__block-title'>
      Modal · size
      <span className='theme-page__note'>
        Portal + Escape + focus restore; `closeOnOverlay` defaults true.
      </span>
    </h3>
    <ModalSizeDemo />

    <h3 className='theme-page__block-title'>Modal · tone</h3>
    <ModalToneDemo />

    <h3 className='theme-page__block-title'>
      ConfirmModal
      <span className='theme-page__note'>
        A `size=&apos;sm&apos;` Modal preset — pair the tone with the confirm variant.
      </span>
    </h3>
    <ConfirmModalDemo />

    {/* ---- Drawer ---- */}
    <h3 className='theme-page__block-title'>
      Drawer
      <span className='theme-page__note'>
        No size/tone props; `closeOnOverlay` defaults <em>false</em> — the opposite of Modal.
      </span>
    </h3>
    <div className='theme-page__row'>
      <DrawerDemo />
    </div>

    {/* ---- Dropdown family ---- */}
    <h3 className='theme-page__block-title'>
      Dropdown
      <span className='theme-page__note'>Alignment uses logical insets, so it flips in RTL.</span>
    </h3>
    <div className='theme-page__variants'>
      <Spec label="align='end' (default)">
        <DropdownDemo />
      </Spec>
      <Spec label="align='start'">
        <DropdownDemo align='start' />
      </Spec>
      <Spec label='portal'>
        <DropdownDemo portal />
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>MultiSectionCheckboxDropdown</h3>
    <div className='theme-page__variants'>
      <Spec label='sections + onClearSection'>
        <CheckboxDropdownDemo />
      </Spec>
      <Spec label='showCount'>
        <CheckboxDropdownDemo showCount />
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>
      FilterDropdown
      <span className='theme-page__note'>
        Two-pane draft filter: edits only reach the caller on Done; Escape/outside-click discards.
      </span>
    </h3>
    <div className='theme-page__variants'>
      <Spec label='categories + searchable + preselection'>
        <FilterDropdownDemo />
      </Spec>
      <Spec label='portal'>
        <FilterDropdownDemo portal />
      </Spec>
    </div>

    <h3 className='theme-page__block-title'>
      SortDropdown
      <span className='theme-page__note'>
        Its `ColumnsMenu` sibling is shown in context, in the table toolbar of section 04.
      </span>
    </h3>
    <div className='theme-page__variants'>
      <Spec label='key list + direction row'>
        <SortDropdownDemo />
      </Spec>
      <Spec label='hideDirection'>
        <SortDropdownDemo hideDirection />
      </Spec>
    </div>

    {/* ---- Tab strips ---- */}
    <h3 className='theme-page__block-title'>
      Tabs
      <span className='theme-page__note'>Underline strip — the count badge renders even at 0.</span>
    </h3>
    <TabsDemo />

    <h3 className='theme-page__block-title'>
      SegmentedControl
      <span className='theme-page__note'>
        Pill toggle — the count is hidden when 0. `variant=&apos;plain&apos;` drops the track; an
        option without `label` becomes a square icon pill named by its `ariaLabel`.
      </span>
    </h3>
    <div className='theme-page__row'>
      <SegmentedDemo />
      <SegmentedPlainDemo />
      <SegmentedIconsDemo />
    </div>

    {/* ---- Icon / Chip closing note ---- */}
    <h3 className='theme-page__block-title'>
      Icon
      <span className='theme-page__note'>
        Kebab-case lucide wrapper. The installed lucide is old — verify a name resolves before use.
      </span>
    </h3>
    <div className='theme-page__row'>
      <Chip variant='neutral' iconStart='info'>
        &lt;Icon name=&apos;kebab-case&apos; size={20} /&gt;
      </Chip>
    </div>
    <div className='theme-page__icons'>
      {ICON_SAMPLER.map((name) => (
        <div key={name} className='theme-page__icon-cell' title={name}>
          <Icon name={name} size={20} />
          <span className='theme-page__token-value'>{name}</span>
        </div>
      ))}
    </div>
  </section>
);

export default OverlaysNavSection;
